/**
 * Admin 「외부 연동」 화면 — 표시 문구·입력 검증(순수 함수).
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — 「구현 계약 › 검증」, D-4·D-7·D-9·D-10
 *
 * 입력 검증은 서버 규칙(계약 「검증」)과 맞춘다. 서버가 최종 판정하고, 여기서는 보내기 전에
 * 같은 규칙으로 막아 사유를 입력 옆에 보여준다.
 */
import type {
  CreatePartnerBody,
  PartnerMeta,
  PartnerSummary,
  UpdatePartnerBody,
  WebhookSecretView,
} from "api/partnerAdminService";

// ---------------------------------------------------------------------------
// 표시 문구
// ---------------------------------------------------------------------------

const ENV_LABEL: Record<string, string> = {
  LOCAL: "로컬",
  DEV: "개발(dev)",
  REL: "운영(rel)",
};

export const envLabel = (env: string | null | undefined): string =>
  (env && ENV_LABEL[env.toUpperCase()]) || env || "환경 알 수 없음";

export const isRelEnv = (env: string | null | undefined): boolean => (env ?? "").toUpperCase() === "REL";

/** D-9 — 운영(REL)에서 발급·폐기·서명 키 변경·비활성 확인 창에 한 줄 더 넣는 문구 */
export const REL_NOTICE = "운영 환경입니다";

export const SECRET_SOURCE_LABEL: Record<WebhookSecretView["source"], string> = {
  db: "DB",
  env: "환경변수",
  constant: "코드 상수",
  none: "없음",
};

export const secretSourceLabel = (source: string | null | undefined): string =>
  (source && SECRET_SOURCE_LABEL[source as WebhookSecretView["source"]]) || source || "없음";

export const DB_SECRET_SOURCE_LABEL: Record<string, string> = {
  input: "입력한 값",
  generated: "생성한 값",
};

export const codeLabel = (code: string | null | undefined, meta?: PartnerMeta): string => {
  if (!code) return "지정 안 됨";
  return meta?.codes.find((c) => c.code === code)?.label ?? code;
};

export const consentProfileLabel = (value: string | null | undefined, meta?: PartnerMeta): string => {
  if (!value) return "선택 안 함";
  return meta?.consentProfiles.find((p) => p.value === value)?.label ?? value;
};

const ACTION_LABEL: Record<string, string> = {
  "partner.create": "파트너 등록",
  "partner.update": "설정 수정",
  "partner.activate": "활성으로 전환",
  "partner.deactivate": "비활성으로 전환",
  "key.issue": "연동 키 발급",
  "key.update": "연동 키 수정",
  "key.revoke": "연동 키 폐기",
  "key.reveal": "원문 복사",
  "key.raw_register": "원문 등록",
  "webhook_secret.set": "서명 키 값 입력",
  "webhook_secret.generate": "서명 키 새로 생성",
  "webhook_secret.clear": "서명 키 DB 값 지움",
  "provision.send": "상대 서버로 전달",
};

export const actionLabel = (action: string): string => ACTION_LABEL[action] ?? action;

const FIELD_LABEL: Record<string, string> = {
  name: "이름",
  code: "종류",
  userId: "연결 계정",
  userEmail: "연결 계정",
  callbackDomains: "콜백 허용 도메인",
  autoReviewOnCollect: "수집 후 자동 분석",
  consentProfile: "동의 화면 프로필",
  provisionUrl: "자동 전달 주소",
  isActive: "상태",
  label: "키 이름",
  ipAllowlist: "허용 IP",
  fingerprint: "지문",
  rawStored: "원문 보관",
  replaced: "덮어씀",
  revokedAt: "폐기 일시",
  reason: "사유",
  source: "출처",
  dbSource: "DB 값 종류",
  ok: "결과",
  status: "응답 코드",
  message: "메시지",
  applied: "적용 항목",
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "없음";
  if (typeof value === "boolean") return value ? "예" : "아니오";
  if (Array.isArray(value)) return value.length === 0 ? "없음" : value.map((v) => formatValue(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const isBeforeAfter = (v: unknown): v is { before: unknown; after: unknown } =>
  !!v && typeof v === "object" && !Array.isArray(v) && ("before" in v || "after" in v);

/**
 * 이력 `changes` 요약 — 계약: 바뀐 칸의 `{ before, after }`(비밀값은 지문만).
 * 형식이 다르면(예: 전달 결과) 키·값을 그대로 풀어 쓴다.
 */
export const summarizeChanges = (changes: unknown): string => {
  if (changes === null || changes === undefined) return "";
  if (typeof changes !== "object" || Array.isArray(changes)) return formatValue(changes);
  return Object.entries(changes as Record<string, unknown>)
    .map(([key, value]) => {
      const label = FIELD_LABEL[key] ?? key;
      if (isBeforeAfter(value)) return `${label}: ${formatValue(value.before)} → ${formatValue(value.after)}`;
      return `${label}: ${formatValue(value)}`;
    })
    .join(" · ");
};

/** 화면 시각 — 브라우저 지역 시각 `YYYY-MM-DD HH:mm`. 값이 없으면 대체 문구. */
export const formatDateTime = (iso: string | null | undefined, empty = "—"): string => {
  if (!iso) return empty;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

// ---------------------------------------------------------------------------
// 입력 검증 — 계약 「검증」
// ---------------------------------------------------------------------------

/** 검증 결과 — ok가 false면 message에 사유가 있다. */
export interface CheckResult {
  ok: boolean;
  message?: string;
}

const OK: CheckResult = { ok: true };
const fail = (message: string): CheckResult => ({ ok: false, message });

/** 여러 줄 입력 → 앞뒤 공백을 지운 비어 있지 않은 줄 목록 */
export const parseLines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

export const NAME_MAX = 100;
export const LABEL_MAX = 50;
export const IP_MAX = 20;
export const DOMAIN_MAX = 10;
export const SECRET_MIN = 16;
export const SECRET_MAX = 256;
export const RAW_KEY_MAX = 256;

/** 등록 원문에 앞뒤 공백·줄바꿈이 있을 때(서버 D13과 같은 문구) */
export const RAW_KEY_WHITESPACE_MESSAGE = "앞뒤 공백·줄바꿈을 지우고 붙여 넣어 주세요";

export const validateName = (name: string): CheckResult => {
  const v = name.trim();
  if (v.length === 0) return fail("이름을 입력하세요.");
  if (v.length > NAME_MAX) return fail(`이름은 ${NAME_MAX}자까지 쓸 수 있습니다.`);
  return OK;
};

export const validateLabel = (label: string): CheckResult => {
  const v = label.trim();
  if (v.length === 0) return fail("키 이름을 입력하세요.");
  if (v.length > LABEL_MAX) return fail(`키 이름은 ${LABEL_MAX}자까지 쓸 수 있습니다.`);
  return OK;
};

export const validateEmail = (email: string): CheckResult => {
  const v = email.trim();
  if (v.length === 0) return fail("연결 계정 이메일을 입력하세요.");
  if (!/^[^\s@]+@[^\s@]+$/.test(v)) return fail("이메일 형식이 아닙니다.");
  return OK;
};

// IPv4 점표기 — 각 칸 0~255, 앞자리 0 없음(Node net.isIPv4와 같은 규칙)
const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export const isIPv4 = (s: string): boolean => IPV4_RE.test(s);

/** IPv6 — 판정은 URL 파서(WHATWG IPv6 규칙)에 맡긴다. 영역 표기(%eth0)는 받지 않는다. */
export const isIPv6 = (s: string): boolean => {
  if (!s.includes(":") || !/^[0-9a-fA-F:.]+$/.test(s)) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(`http://[${s}]/`);
    return true;
  } catch {
    return false;
  }
};

export const isIpOrWildcard = (s: string): boolean => s === "*" || isIPv4(s) || isIPv6(s);

/** D-7 — 모든 IP 허용(`*`)을 고를 때 보이는 경고 */
export const WILDCARD_IP_WARNING =
  "모든 IP에서 이 키로 호출할 수 있습니다. 고정 IP가 없는 서버 간 연동에만 쓰세요";

/**
 * 허용 IP — 1~20개, 각 줄은 `*` 또는 IPv4·IPv6 정확값. CIDR은 받지 않는다(인증 코드가 정확 일치만 한다).
 * 빈 목록은 "전체 거부"라 키가 조용히 쓸모없어지므로 막는다(D-7).
 */
export const validateIpAllowlist = (ips: string[]): CheckResult => {
  if (ips.length === 0) return fail("허용 IP를 한 줄에 하나 이상 입력하세요.");
  if (ips.length > IP_MAX) return fail(`허용 IP는 ${IP_MAX}개까지 넣을 수 있습니다.`);
  const cidr = ips.find((ip) => ip.includes("/"));
  if (cidr) return fail(`CIDR 범위는 쓸 수 없습니다. IP를 한 줄에 하나씩 적으세요: ${cidr}`);
  const bad = ips.filter((ip) => !isIpOrWildcard(ip));
  if (bad.length > 0) return fail(`IP 형식이 아닙니다: ${bad.join(", ")}`);
  return OK;
};

const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;

/** 콜백 허용 도메인 — 서버 규칙이 소문자 호스트명이라 보내기 전에 소문자로 바꾼다. */
export const normalizeDomains = (lines: string[]): string[] => lines.map((d) => d.toLowerCase());

/** 콜백 허용 도메인 — 0~10개, 호스트명만(스킴·포트·경로 없음). `normalizeDomains`를 거친 값을 넣는다. */
export const validateCallbackDomains = (domains: string[]): CheckResult => {
  if (domains.length > DOMAIN_MAX) return fail(`콜백 허용 도메인은 ${DOMAIN_MAX}개까지 넣을 수 있습니다.`);
  for (const d of domains) {
    if (d.includes("://")) return fail(`https:// 없이 호스트명만 적으세요: ${d}`);
    if (d.includes("/")) return fail(`경로 없이 호스트명만 적으세요: ${d}`);
    if (d.includes(":")) return fail(`포트 없이 호스트명만 적으세요: ${d}`);
    if (!HOSTNAME_RE.test(d)) return fail(`호스트명 형식이 아닙니다: ${d}`);
  }
  return OK;
};

const hostInDomains = (host: string, domains: string[]): boolean =>
  domains.some((d) => host === d || host.endsWith(`.${d}`));

/**
 * 자동 전달 주소 — 비우면 null. 값이 있으면 `https://` + 호스트가 콜백 허용 도메인 안(D-11).
 * 도메인 일치는 기존 콜백 검증과 같은 방식(같은 호스트 또는 그 하위 도메인)으로 본다.
 */
export const validateProvisionUrl = (url: string, domains: string[]): CheckResult => {
  const v = url.trim();
  if (v.length === 0) return OK;
  if (!/^https:\/\//i.test(v)) return fail("자동 전달 주소는 https://로 시작해야 합니다.");
  let host: string;
  try {
    host = new URL(v).hostname.toLowerCase();
  } catch {
    return fail("자동 전달 주소가 URL 형식이 아닙니다.");
  }
  if (!hostInDomains(host, domains)) {
    return fail("자동 전달 주소의 호스트가 콜백 허용 도메인에 없습니다.");
  }
  return OK;
};

/**
 * 웹훅 서명 키 직접 입력 — 16~256자 출력 가능 ASCII.
 * 앞뒤 공백은 복사·붙여넣기 실수일 때가 많고, 그대로 저장하면 서명이 맞지 않아 결과 통지가 조용히
 * 거절되므로 막는다.
 */
export const validateWebhookSecret = (secret: string): CheckResult => {
  if (secret.length < SECRET_MIN || secret.length > SECRET_MAX) {
    return fail(`서명 키는 ${SECRET_MIN}~${SECRET_MAX}자여야 합니다. 지금 ${secret.length}자입니다.`);
  }
  if (!/^[\x20-\x7E]+$/.test(secret)) return fail("서명 키에는 영문·숫자·기호(출력 가능한 ASCII)만 쓸 수 있습니다.");
  if (secret !== secret.trim()) return fail("서명 키 앞뒤에 공백이 있습니다. 복사한 값을 확인하세요.");
  return OK;
};

/**
 * 연동 키 원문 등록 — 보내기 전 사전 검증(서버 규칙 D13과 같은 관문, 지문 일치만 서버가 최종 판정).
 * - 빈 값은 막는다.
 * - 앞뒤 공백·줄바꿈은 서버 호출 없이 막는다(복사·붙여넣기 사고 — trim해서 저장하지 않으므로 지문이 어긋난다).
 * - 256자 초과·출력 불가 ASCII는 막는다. 최소 길이는 두지 않는다(해시 일치가 실질 관문).
 */
export const validateRawKeyInput = (rawKey: string): CheckResult => {
  if (rawKey.length === 0) return fail("원문을 붙여 넣으세요.");
  if (rawKey !== rawKey.trim() || /[\r\n]/.test(rawKey)) return fail(RAW_KEY_WHITESPACE_MESSAGE);
  if (rawKey.length > RAW_KEY_MAX) return fail(`원문은 ${RAW_KEY_MAX}자까지 쓸 수 있습니다. 지금 ${rawKey.length}자입니다.`);
  if (!/^[\x20-\x7E]+$/.test(rawKey)) return fail("원문에는 영문·숫자·기호(출력 가능한 ASCII)만 쓸 수 있습니다.");
  return OK;
};

// ---------------------------------------------------------------------------
// 파트너 폼 → 요청 본문
// ---------------------------------------------------------------------------

/** 파트너 등록·수정 창이 올리는 정리된 입력값 */
export interface PartnerFormValues {
  name: string;
  code: string;
  userEmail: string;
  consentProfile: string | null;
  callbackDomains: string[];
  provisionUrl: string | null;
  autoReviewOnCollect: boolean;
}

/** POST /admin/partners 본문 — 비어 있는 선택 칸(동의 프로필·자동 전달 주소)은 싣지 않는다. */
export const buildCreateBody = (v: PartnerFormValues): CreatePartnerBody => {
  const body: CreatePartnerBody = {
    name: v.name,
    code: v.code,
    userEmail: v.userEmail,
    callbackDomains: v.callbackDomains,
    autoReviewOnCollect: v.autoReviewOnCollect,
  };
  if (v.consentProfile) body.consentProfile = v.consentProfile;
  if (v.provisionUrl) body.provisionUrl = v.provisionUrl;
  return body;
};

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * PATCH /admin/partners/:id 본문 — 바뀐 칸만 싣는다. 비운 선택 칸은 null로 보내 지운다.
 * 바뀐 것이 없으면 빈 객체.
 */
export const buildUpdateBody = (partner: PartnerSummary, v: PartnerFormValues): UpdatePartnerBody => {
  const body: UpdatePartnerBody = {};
  if (v.name !== partner.name) body.name = v.name;
  if (!sameList(v.callbackDomains, partner.callbackDomains ?? [])) body.callbackDomains = v.callbackDomains;
  if (v.autoReviewOnCollect !== partner.autoReviewOnCollect) body.autoReviewOnCollect = v.autoReviewOnCollect;
  if ((v.consentProfile ?? null) !== (partner.consentProfile ?? null)) body.consentProfile = v.consentProfile;
  if ((v.provisionUrl ?? null) !== (partner.provisionUrl ?? null)) body.provisionUrl = v.provisionUrl;
  return body;
};
